/**
 * 月ごとの利用状況（issue #115・docs/01 15-4）。
 *
 * **月を切り替えて見る1枚の画面**です。月1回の利用状況の共有は、この画面をご覧いただくことで行います。
 *
 * 出すもの: 使った人数（拠点ごと・全体）／メッセージの数の推移（6か月）／AI先輩／お知らせの未読／休眠の件数。
 *
 * 決めごと:
 *  - **人数と件数だけ。** どなたが何件送ったか・どなたが使ったか（個人の活動量）は出しません。
 *  - **1対1のトークは出てきません。** メッセージの数は全体の件数に含めるだけで、トークごとには分けません。
 *  - 受け持ちが自拠点の方（拠点管理者など）には、自拠点の分だけが出ます（サーバが数える範囲を決める）。
 *  - 欄ごとに、その欄の「できること」を持っている方にだけ出ます（持っていなければサーバが空で返す）。
 *  - 月は「前の月」「次の月」のボタンで切り替えます（押したら、その月の数字に変わる・決定22）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import type { UsageReport } from '../api/types.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { count, monthLabel, shiftMonth, shortMonthLabel } from '../dormancy/labels.js';
import { formatTableDateTime } from '../format/datetime.js';

export function UsagePage(): ReactNode {
  const api = useApi();
  /** 見ている月（空＝今月。サーバが今月を決める） */
  const [month, setMonth] = useState<string | null>(null);
  const [data, setData] = useState<UsageReport | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    api
      .get<UsageReport>('/admin/usage', { query: month ? { month } : {} })
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, month, reloadKey]);

  const shown = data?.month ?? month;
  const isLatest = data !== null && data.month >= data.latest_month;

  return (
    <article>
      <PageHeader
        title="月ごとの利用状況"
        description="月ごとの人数と件数です。どなたが使ったか・何件送ったかは出ません。1対1のトークの中身や相手も出ません。"
      />

      <div className="admin-month-switch" role="group" aria-label="月の切り替え">
        <Button
          variant="secondary"
          compact
          disabled={shown === null}
          onClick={() => shown && setMonth(shiftMonth(shown, -1))}
        >
          ← 前の月
        </Button>
        <h2 className="admin-month-label" aria-live="polite">
          {shown ? monthLabel(shown) : '今月'}
        </h2>
        <Button
          variant="secondary"
          compact
          disabled={shown === null || isLatest}
          onClick={() => shown && setMonth(shiftMonth(shown, 1))}
        >
          次の月 →
        </Button>
      </div>

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !data ? <LoadingBlock label="利用状況を読み込んでいます…" /> : null}

      {data ? (
        <>
          {data.scope === 'site' ? (
            <p className="admin-note">受け持ちの拠点の分だけを数えています。</p>
          ) : null}
          {data.people ? <PeopleCard people={data.people} /> : null}
          {data.posts ? <PostsCard months={data.posts.months} /> : null}
          {data.ai ? <AiCard ai={data.ai} /> : null}
          {data.announcements ? <AnnouncementsCard notices={data.announcements} /> : null}
          <DormancyCard dormancy={data.dormancy} />
        </>
      ) : null}
    </article>
  );
}

function PeopleCard({ people }: { people: NonNullable<UsageReport['people']> }): ReactNode {
  return (
    <Card
      title="使った人数"
      description="その月にアプリか管理画面をお使いになった方の人数です（AI先輩は数えません）。メンバーの人数は、いまの人数です。"
    >
      <DataTable label="拠点ごとの使った人数">
        <thead>
          <tr>
            <th scope="col">拠点</th>
            <th scope="col" className="ui-cell-num">
              使った人数
            </th>
            <th scope="col" className="ui-cell-num">
              メンバーの人数（いま）
            </th>
          </tr>
        </thead>
        <tbody>
          {people.sites.map((row) => (
            <tr key={row.site_id ?? 'none'}>
              <th scope="row">{row.site_name ?? '拠点なし'}</th>
              <td className="ui-cell-num">{count(row.used)}名</td>
              <td className="ui-cell-num">{count(row.roster)}名</td>
            </tr>
          ))}
          {people.sites.length > 1 ? (
            <tr>
              <th scope="row">全体</th>
              <td className="ui-cell-num">{count(people.total.used)}名</td>
              <td className="ui-cell-num">{count(people.total.roster)}名</td>
            </tr>
          ) : null}
        </tbody>
      </DataTable>
    </Card>
  );
}

function PostsCard({ months }: { months: { month: string; count: number }[] }): ReactNode {
  const max = Math.max(1, ...months.map((row) => row.count));
  return (
    <Card
      title="メッセージの数（6か月の移り変わり）"
      description="人が送ったメッセージの数です。送信を取り消したもの・保存期間を過ぎて消えたものは数えません。"
    >
      <DataTable label="月ごとのメッセージの数">
        <thead>
          <tr>
            <th scope="col">月</th>
            <th scope="col" className="ui-cell-num">
              メッセージ
            </th>
            <th scope="col">
              <span className="admin-visually-hidden">棒グラフ</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {months.map((row) => (
            <tr key={row.month}>
              <th scope="row">{shortMonthLabel(row.month)}</th>
              <td className="ui-cell-num">{count(row.count)}件</td>
              <td className="admin-bar-cell" aria-hidden="true">
                <span
                  className="admin-bar"
                  style={{ width: `${Math.round((row.count / max) * 100)}%` }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  );
}

function AiCard({ ai }: { ai: NonNullable<UsageReport['ai']> }): ReactNode {
  const stats: { label: string; value: number; unit: string }[] = [
    { label: '質問した人数', value: ai.people, unit: '名' },
    { label: '質問', value: ai.asked, unit: '件' },
    { label: '答えられた', value: ai.answered, unit: '件' },
    { label: '担当の方へ案内した', value: ai.guided, unit: '件' },
    { label: '答えられなかった', value: ai.unanswered, unit: '件' },
  ];
  return (
    <Card
      title="AI先輩への質問"
      description="労務・健康など繊細な質問は、件数にだけ入ります（中身は出ません）。"
      actions={<Link to="/ai/usage">AI先輩の利用状況（月ごとの一覧）</Link>}
    >
      <ul className="admin-stats" aria-label="AI先輩の数字">
        {stats.map((stat) => (
          <li key={stat.label} className="admin-stat">
            <span className="admin-stat-label">{stat.label}</span>
            <span className="admin-stat-value">
              {count(stat.value)}
              <small>{stat.unit}</small>
            </span>
          </li>
        ))}
      </ul>
      {ai.top_questions.length > 0 ? (
        <>
          <h3 className="admin-subheading">よく聞かれた質問</h3>
          <ol className="admin-ai-top">
            {ai.top_questions.map((row) => (
              <li key={row.question}>
                {row.question}
                <small>{count(row.count)}件</small>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </Card>
  );
}

function AnnouncementsCard({
  notices,
}: {
  notices: NonNullable<UsageReport['announcements']>;
}): ReactNode {
  return (
    <Card
      title="お知らせの未読"
      description="その月に送ったお知らせごとの既読・未読の人数です。どなたが未読かは出ません（未読の方への再通知は「お知らせ」の画面から行えます）。"
      actions={<Link to="/notices">お知らせの画面へ</Link>}
    >
      {notices.sent === 0 ? (
        <p className="admin-note">この月に送ったお知らせはありません。</p>
      ) : (
        <>
          <p className="admin-note">
            {count(notices.sent)}件のお知らせで、宛先はのべ{count(notices.total)}名、そのうち
            {count(notices.unread_count)}名が未読です。
          </p>
          <DataTable label="お知らせごとの未読">
            <thead>
              <tr>
                <th scope="col">送った日時</th>
                <th scope="col">トーク</th>
                <th scope="col" className="ui-cell-num">
                  宛先
                </th>
                <th scope="col" className="ui-cell-num">
                  既読
                </th>
                <th scope="col" className="ui-cell-num">
                  未読
                </th>
              </tr>
            </thead>
            <tbody>
              {notices.items.map((row) => (
                <tr key={row.id}>
                  <td className="ui-cell-date">{formatTableDateTime(row.sent_at)}</td>
                  <th scope="row" className="ui-cell-wrap">
                    {row.channel_name}
                  </th>
                  <td className="ui-cell-num">{count(row.total)}名</td>
                  <td className="ui-cell-num">{count(row.read_count)}名</td>
                  <td className="ui-cell-num">{count(row.unread_count)}名</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          {notices.truncated ? (
            <p className="admin-note">
              新しい順に{count(notices.items.length)}件を出しています（上の人数は全部の合計です）。
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function DormancyCard({ dormancy }: { dormancy: UsageReport['dormancy'] }): ReactNode {
  const rows: { label: string; value: number; unit: string; to: string }[] = [];
  if (dormancy.members !== null) {
    rows.push({
      label: '長くお使いでない方',
      value: dormancy.members,
      unit: '名',
      to: '/dormancy/members',
    });
  }
  if (dormancy.channel_candidates !== null) {
    rows.push({
      label: '長く使われていないトーク',
      value: dormancy.channel_candidates,
      unit: '件',
      to: '/dormancy/rooms',
    });
  }
  if (dormancy.dormant_channels !== null) {
    rows.push({
      label: '休眠にしたトーク',
      value: dormancy.dormant_channels,
      unit: '件',
      to: '/dormancy/rooms',
    });
  }
  if (rows.length === 0) return null;
  return (
    <Card
      title="休眠の件数（いま）"
      description="月にかかわらず、いまの件数です。確かめて片付けるときは、それぞれの画面から行います。"
    >
      <ul className="admin-stats" aria-label="休眠の件数">
        {rows.map((row) => (
          <li key={row.label} className="admin-stat">
            <span className="admin-stat-label">{row.label}</span>
            <span className="admin-stat-value">
              {count(row.value)}
              <small>{row.unit}</small>
            </span>
            <Link className="admin-stat-note" to={row.to}>
              一覧を開く
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
